import type { KryspaceRenderManifest } from '../../../types/kryspaceRenderManifest';
import { fixUtf8Mojibake } from '../fixUtf8Mojibake';
import { entropyFromCorrectionFactor, entropyStateLabel } from '../entropyLanguage';

export type McDomainKey = 'business' | 'stakeholder' | 'customer';
export type McEntropyClass = 'computed' | 'dormant' | 'undefined';

export interface McDomainVgrTrendPoint {
  periodIndex: number;
  value: number;
}

export interface McDomainVgrTile {
  domain: McDomainKey;
  title: string;
  vgrValue: number | null;
  valueGeneratedActual: number | null;
  costToGenerateActual: number | null;
  trendSeries: McDomainVgrTrendPoint[];
  entropyClass: McEntropyClass;
  entropyScore: number | null;
  /** Reserved-band label from Kryspace (e.g. "Not yet scored"); never a numeric 8–10. */
  clientEntropyLabel?: string | null;
  mostCriticalRank: number | null;
}

export interface McDomainVgrModel {
  tiles: McDomainVgrTile[];
  mostCriticalDomain: McDomainKey | null;
}

/** Minimal scope-health row shape for proxy fallback. */
export interface ScopeHealthFallbackRow {
  itemId?: string;
  title?: string;
  domain?: string;
  correctionFactor?: number;
  vgrCorrectionFactor?: number;
  entropy?: number;
  valueGeneratedActual?: number;
  costToGenerateActual?: number;
}

const DOMAIN_ORDER: McDomainKey[] = ['business', 'stakeholder', 'customer'];

function asDomain(value: unknown): McDomainKey | null {
  if (value === 'business' || value === 'stakeholder' || value === 'customer') {
    return value;
  }
  return null;
}

function asEntropyClass(value: unknown): McEntropyClass {
  if (value === 'computed' || value === 'dormant' || value === 'undefined') {
    return value;
  }
  return 'undefined';
}

function ratioFromActuals(value: number | null, cost: number | null): number | null {
  if (value == null || cost == null || !(cost > 0)) return null;
  return Math.round((value / cost) * 10000) / 10000;
}

/** Headline is value generated / cost. A stored correction factor is not the ratio. */
function headlineVgr(
  raw: Record<string, unknown>,
  valueGenerated: number | null,
  cost: number | null
): number | null {
  const fromActuals = ratioFromActuals(valueGenerated, cost);
  if (fromActuals != null) return fromActuals;
  return (
    numericField(raw, 'vgrValue') ??
    numericField(raw, 'valueGenerationRatio') ??
    numericField(raw, 'value_generation_ratio')
  );
}

function numericField(row: Record<string, unknown>, key: string): number | null {
  const raw = row[key];
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseTrendSeries(raw: unknown): McDomainVgrTrendPoint[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((point) => {
      if (!point || typeof point !== 'object') return null;
      const row = point as Record<string, unknown>;
      const value = numericField(row, 'value');
      const periodIndex = numericField(row, 'periodIndex');
      if (value == null || periodIndex == null) return null;
      return { periodIndex, value };
    })
    .filter((p): p is McDomainVgrTrendPoint => p != null)
    .sort((a, b) => a.periodIndex - b.periodIndex);
}

export function parseMcDomainVgrFromManifest(manifest: KryspaceRenderManifest): McDomainVgrModel | null {
  const section = manifest.sections.find((s) => s.id === 'mc-domain-vgr');
  if (!section?.queue?.length) return null;

  const tiles = section.queue
    .map((row): McDomainVgrTile | null => {
      const raw = row as unknown as Record<string, unknown>;
      const domain = asDomain(raw.domain ?? raw.itemId);
      if (!domain) return null;
      const valueGeneratedActual =
        numericField(raw, 'valueGeneratedActual') ?? numericField(raw, 'value_generated_actual');
      const costToGenerateActual =
        numericField(raw, 'costToGenerateActual') ?? numericField(raw, 'cost_to_generate_actual');
      return {
        domain,
        title: fixUtf8Mojibake(typeof row.title === 'string' ? row.title : domain),
        vgrValue: headlineVgr(raw, valueGeneratedActual, costToGenerateActual),
        valueGeneratedActual,
        costToGenerateActual,
        trendSeries: parseTrendSeries(raw.trendSeries),
        entropyClass: asEntropyClass(raw.entropyClass),
        entropyScore: numericField(raw, 'entropyScore') ?? row.entropy ?? null,
        clientEntropyLabel:
          typeof raw.clientEntropyLabel === 'string'
            ? raw.clientEntropyLabel
            : typeof raw.client_entropy_label === 'string'
              ? raw.client_entropy_label
              : null,
        mostCriticalRank: numericField(raw, 'mostCriticalRank'),
      };
    })
    .filter((t): t is McDomainVgrTile => t != null);

  if (!tiles.length) return null;

  const orderedTiles = DOMAIN_ORDER.map(
    (domain) =>
      tiles.find((t) => t.domain === domain) ?? {
        domain,
        title: domain.charAt(0).toUpperCase() + domain.slice(1),
        vgrValue: null,
        valueGeneratedActual: null,
        costToGenerateActual: null,
        trendSeries: [],
        entropyClass: 'undefined' as const,
        entropyScore: null,
        clientEntropyLabel: null,
        mostCriticalRank: null,
      }
  );

  const meta = section.meta as Record<string, unknown> | undefined;
  const mostCriticalDomain =
    asDomain(meta?.mostCriticalDomain) ??
    orderedTiles.find((t) => t.mostCriticalRank === 1)?.domain ??
    null;

  return {
    tiles: orderedTiles,
    mostCriticalDomain,
  };
}

function domainKeyFromScope(row: ScopeHealthFallbackRow): McDomainKey | null {
  const blob = `${row.itemId ?? ''} ${row.title ?? ''} ${row.domain ?? ''}`.toLowerCase();
  if (blob.includes('stakeholder')) return 'stakeholder';
  if (blob.includes('customer')) return 'customer';
  if (blob.includes('business')) return 'business';
  return null;
}

/** Proxy fallback when mc-domain-vgr section absent (scope_health subdomain rows). */
export function deriveMcDomainVgrFallback(scopeHealthQueue: ScopeHealthFallbackRow[]): McDomainVgrModel {
  const buckets: Record<McDomainKey, ScopeHealthFallbackRow[]> = {
    business: [],
    stakeholder: [],
    customer: [],
  };
  for (const row of scopeHealthQueue) {
    const key = domainKeyFromScope(row);
    if (key) {
      buckets[key].push(row);
    }
  }

  const tiles: McDomainVgrTile[] = DOMAIN_ORDER.map((domain) => {
    const rows = buckets[domain];
    const entropies = rows
      .map((r) => r.entropy)
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    const entropyScore =
      entropies.length > 0 ? entropies.reduce((a, b) => a + b, 0) / entropies.length : null;
    const valueGenerated = rows
      .map((r) => {
        const raw = r as Record<string, unknown>;
        const v = r.valueGeneratedActual ?? raw.value_generated_actual;
        return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
      })
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    const costToGenerate = rows
      .map((r) => {
        const raw = r as Record<string, unknown>;
        const v = r.costToGenerateActual ?? raw.cost_to_generate_actual;
        return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
      })
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    const valueGeneratedActual =
      valueGenerated.length > 0 ? valueGenerated.reduce((a, b) => a + b, 0) : null;
    const costToGenerateActual =
      costToGenerate.length > 0 ? costToGenerate.reduce((a, b) => a + b, 0) : null;
    const vgrValue = ratioFromActuals(valueGeneratedActual, costToGenerateActual);
    return {
      domain,
      title: domain.charAt(0).toUpperCase() + domain.slice(1),
      vgrValue,
      valueGeneratedActual,
      costToGenerateActual,
      trendSeries: vgrValue != null ? [{ periodIndex: 0, value: vgrValue }] : [],
      entropyClass: vgrValue != null ? 'computed' : 'undefined',
      entropyScore,
      clientEntropyLabel: null,
      mostCriticalRank: null,
    };
  });

  const ranked = [...tiles]
    .filter((t) => t.vgrValue != null)
    .sort((a, b) => (a.vgrValue ?? 1) - (b.vgrValue ?? 1));
  ranked.forEach((tile, index) => {
    tile.mostCriticalRank = index + 1;
  });

  const mostCriticalDomain = ranked[0]?.domain ?? null;

  return { tiles, mostCriticalDomain };
}

export function formatVgrHeadline(vgrValue: number | null): string {
  if (vgrValue == null || Number.isNaN(vgrValue)) return '—';
  return formatVgrRatio(vgrValue);
}

/** Non-MC compact band: never alarm on missing VGR (Orlin C7/C8). */
export function formatVgrGlanceValue(vgrValue: number | null): {
  text: string;
  derived: boolean;
} {
  if (vgrValue == null || Number.isNaN(vgrValue)) {
    return { text: 'Not yet derived', derived: false };
  }
  return { text: formatVgrRatio(vgrValue), derived: true };
}

/** Entropy presentation for compact glance — muted when VGR/entropy not derived. */
export function glanceEntropyPresentation(tile: McDomainVgrTile): {
  tone: 'good' | 'mid' | 'weak' | 'muted';
  title: string;
  score: number | null;
} {
  if (tile.entropyClass === 'undefined' || tile.vgrValue == null || Number.isNaN(tile.vgrValue)) {
    return {
      tone: 'muted',
      title: 'VGR / entropy not yet derived (model chain incomplete)',
      score: null,
    };
  }
  if (tile.entropyClass === 'dormant' && tile.entropyScore == null) {
    return {
      tone: 'muted',
      title: 'Entropy dormant — not yet assessed as an alarm',
      score: null,
    };
  }
  const score = entropyDisplayScore(tile);
  if (score == null) {
    return { tone: 'muted', title: 'Entropy not assessed', score: null };
  }
  const tone =
    score <= 4 ? 'good' : score <= 6 ? 'mid' : 'weak';
  return { tone, title: `Entropy ${score}`, score };
}

/** VGR correction factor as a ratio number (not a percent). */
export function formatVgrRatio(vgrValue: number | null): string {
  if (vgrValue == null || Number.isNaN(vgrValue)) return '—';
  return vgrValue.toFixed(2);
}

export function formatVgrComponent(value: number | null): string {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
}

export type McTrendDirection = 'up' | 'down' | 'flat' | 'unknown';

export function vgrTrendDirection(series: McDomainVgrTrendPoint[]): McTrendDirection {
  if (series.length < 2) return series.length === 1 ? 'flat' : 'unknown';
  const delta = series[series.length - 1].value - series[series.length - 2].value;
  if (Math.abs(delta) < 0.01) return 'flat';
  return delta > 0 ? 'up' : 'down';
}

export function entropyDisplayScore(tile: McDomainVgrTile): number | null {
  if (tile.entropyClass === 'dormant' || tile.entropyClass === 'undefined') return null;
  if (tile.clientEntropyLabel?.trim()) return null;
  if (tile.vgrValue != null && Number.isFinite(tile.vgrValue)) {
    return entropyFromCorrectionFactor(tile.vgrValue);
  }
  return null;
}

export function entropyDisplayLabel(tile: McDomainVgrTile): string | null {
  if (tile.clientEntropyLabel?.trim()) return tile.clientEntropyLabel.trim();
  if (tile.entropyClass === 'dormant') return 'ACTIVE unmeasurable';
  if (tile.entropyClass === 'undefined') return 'DRAFTED';
  return entropyStateLabel(tile.entropyScore);
}

export function trendDirectionLabel(direction: McTrendDirection): string {
  switch (direction) {
    case 'up':
      return 'Trending up';
    case 'down':
      return 'Trending down';
    case 'flat':
      return 'Stable';
    default:
      return 'Trend not available';
  }
}
